import { useState, useEffect } from 'react'
import { SystemParameter, TradingParameters } from '@/types'
import '../styles/TabStyles.css'

interface TradingParametersTabProps {
  parameters: SystemParameter[]
  onUpdate: (key: string, value: string) => Promise<void>
}

interface ParameterField {
  key: string
  label: string
  type: 'number' | 'percentage'
  min: number
  max: number
  step: number
  description: string
}

const PARAMETER_FIELDS: ParameterField[] = [
  {
    key: 'min_trade_amount',
    label: '最小交易金额',
    type: 'number',
    min: 0,
    max: 1000000,
    step: 1,
    description: '用户单笔交易的最小金额',
  },
  {
    key: 'max_trade_amount',
    label: '最大交易金额',
    type: 'number',
    min: 0,
    max: 10000000,
    step: 1,
    description: '用户单笔交易的最大金额',
  },
  {
    key: 'max_leverage',
    label: '最大杠杆倍数',
    type: 'number',
    min: 1,
    max: 500,
    step: 1,
    description: '用户可使用的最大杠杆倍数',
  },
  {
    key: 'risk_warning_rate',
    label: '风险率警告线',
    type: 'percentage',
    min: 0,
    max: 100,
    step: 1,
    description: '风险率达到此值时发送警告',
  },
  {
    key: 'risk_liquidation_rate',
    label: '风险率平仓线',
    type: 'percentage',
    min: 0,
    max: 100,
    step: 1,
    description: '风险率达到此值时自动平仓',
  },
  {
    key: 'min_account_balance',
    label: '最小账户余额',
    type: 'number',
    min: 0,
    max: 1000000,
    step: 1,
    description: '账户保持的最小余额',
  },
  {
    key: 'trading_commission',
    label: '交易手续费',
    type: 'percentage',
    min: 0,
    max: 100,
    step: 0.01,
    description: '每笔交易收取的手续费百分比',
  },
]

export default function TradingParametersTab({ parameters, onUpdate }: TradingParametersTabProps) {
  const [values, setValues] = useState<Record<string, number>>({})
  const [editing, setEditing] = useState(false)
  const [tempValues, setTempValues] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load parameters
  useEffect(() => {
    const newValues: Record<string, number> = {}
    PARAMETER_FIELDS.forEach(field => {
      const param = parameters.find(p => p.param_key === field.key)
      if (param) {
        newValues[field.key] = parseFloat(param.param_value)
      }
    })
    setValues(newValues)
    setTempValues(newValues)
  }, [parameters])

  const handleEdit = () => {
    setTempValues(values)
    setEditing(true)
  }

  const handleCancel = () => {
    setEditing(false)
    setError(null)
  }

  const handleSave = async () => {
    // Validation
    const minAmount = tempValues['min_trade_amount'] || 0
    const maxAmount = tempValues['max_trade_amount'] || 0

    if (maxAmount < minAmount) {
      setError('最大交易金额不能小于最小交易金额')
      return
    }

    setLoading(true)
    try {
      // Update all parameters
      for (const field of PARAMETER_FIELDS) {
        const value = tempValues[field.key]
        if (value !== undefined) {
          await onUpdate(field.key, value.toString())
        }
      }
      setValues(tempValues)
      setEditing(false)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setLoading(false)
    }
  }

  const handleValueChange = (key: string, value: number) => {
    setTempValues({ ...tempValues, [key]: value })
  }

  return (
    <div className="tab-panel">
      <div className="panel-header">
        <h3>交易参数配置</h3>
        {!editing && (
          <button className="btn-primary" onClick={handleEdit}>
            编辑
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {editing ? (
        <div className="edit-form">
          <div className="form-grid">
            {PARAMETER_FIELDS.map(field => (
              <div key={field.key} className="form-group">
                <label>{field.label}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={tempValues[field.key] || 0}
                    onChange={e => handleValueChange(field.key, parseFloat(e.target.value))}
                    placeholder={`输入${field.label}`}
                  />
                  {field.type === 'percentage' && <span className="unit">%</span>}
                </div>
                <small>{field.description}</small>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
              取消
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      ) : (
        <div className="parameters-grid">
          {PARAMETER_FIELDS.map(field => (
            <div key={field.key} className="parameter-card">
              <div className="param-label">{field.label}</div>
              <div className="param-value">
                {values[field.key]?.toFixed(field.type === 'percentage' ? 2 : 0)}
                {field.type === 'percentage' && '%'}
              </div>
              <div className="param-description">{field.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
